import React, {
    ReactElement, createContext, useContext, useState, useRef, useMemo,
    FC, useEffect
} from 'react'
import type { UploadProps } from 'antd'
import { Space, Button, Table, Upload, Form, Input, Popover } from 'antd'
import type { FormInstance } from 'antd/es/form'
import type { InputRef } from 'antd'
import { marked } from 'marked'
import { useTranslation } from 'react-i18next'
import { UploadOutlined, DownloadOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import * as XLSX from 'xlsx'
import { relativeTopic, getTechlId } from '@/services/rag'
import { autoClick } from '@/utils/util'

const renderer = new marked.Renderer()
const { ...defaults } = marked.getDefaults()

interface DataType {
    key: string
    requirementName: string
    functionDesc: string
    relativeTech: string
    techIndicators: string
    relativeTopic: string[]
    loading?: boolean
}
interface EditableRowProps {
    index: number
}
interface EditableCellProps {
    editable: boolean;
    children: React.ReactNode;
    dataIndex: keyof DataType;
    record: DataType;
    // eslint-disable-next-line no-unused-vars
    handleSave: (record: DataType) => void;
  }
type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const EditableContext = createContext<FormInstance<any> | null>(null)
const DataSourceContext = createContext<{
    dataSource: DataType[]
    setDataSource: any
} | null>(null)

const EditableRow: FC<EditableRowProps> = ({ ...props }) => {
    const [form] = Form.useForm()
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    )
}
const EditableCell: React.FC<EditableCellProps> = ({
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false)
    const inputRef = useRef<InputRef>(null)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const form = useContext(EditableContext)!
  
    useEffect(() => {
        if (editing) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            inputRef.current!.focus()
        }
    }, [editing])
  
    const toggleEdit = () => {
        setEditing(!editing)
        form.setFieldsValue({ [dataIndex]: record[dataIndex] })
    }
  
    const save = async () => {
        try {
            const values = await form.validateFields()
  
            toggleEdit()
            handleSave({ ...record, ...values })
        } catch (errInfo) {
            console.log('Save failed:', errInfo)
        }
    }
  
    let childNode = children
  
    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{ margin: 0 }}
                name={dataIndex}
            >
                <Input.TextArea ref={inputRef} autoSize={{ minRows: 2, maxRows: 6 }} onBlur={save} />
            </Form.Item>
        ) : (
            <div className="editable-cell-value-wrap" style={{ paddingRight: 24 }} onClick={toggleEdit}>
                {children}
            </div>
        )
    }
  
    return <td {...restProps}>{childNode}</td>
}

const BatchRelativeTech = ({ winH }: {
    winH: number
}): ReactElement => {
    const { t, i18n } = useTranslation()
    const [dataSource, setDataSource] = useState<DataType[]>([])
    const dataScourceRef = useRef<DataType[]>([])

    const btnLoading = useMemo(() =>
        dataSource.some(item => item.loading === true)
    , [dataSource])


    const description$tokens = useRef(
        marked.lexer(`
1. **需求名称**
- **清晰简明地描述需求名称**，易于辨识。
- 示例：如“夜视功能”或“自动泊车定位”。
2. **功能描述**
- **概述需求的主要功能**和使用场景。
- 示例：如“提升夜间行车安全，通过红外功能提高低光环境下的可视性”。
3. **相关技术**
- **列出实现需求的关键技术**，如“计算机视觉”或“深度学习算法”。
- 示例：如“基于YOLO的物体识别”。
4. **技术指标**
- **设定具体的技术指标**，便于评估和验收。
- 示例：如“识别准确率≥95%，响应时间≤50ms”。
        `)
    )

    const gotoDetail = async (ptn: string) => {
        const resData = await getTechlId({
            ptns: [ptn]
        })
        autoClick(`/projectInfoView?workGroupId=1&projectId=${resData[ptn]}&isView=true`)
    }

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    }

    const renderAction = (_: string, record: any) => {
        return (<>
            <Button type="primary" ghost
                loading={!!record.loading}
                onClick={() => {
                    onRelativeTopic(record)
                }}
            >{t('Tech Relative Project')}</Button>
        </>)
    }

    const handleSave = (row: DataType) => {
        const newData = [...dataSource]
        const index = newData.findIndex(item => row.key === item.key)
        const item = newData[index]
        newData.splice(index, 1, {
            ...item,
            ...row,
        })
        setDataSource(newData)
    }

    const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
        {
            title: t('requirementName'),
            dataIndex: 'requirementName',
            key: 'requirementName',
            width: '20%',
            editable: true
        },
        {
            title: t('functionDesc'),
            dataIndex: 'functionDesc',
            key: 'functionDesc',
            width: '20%',
            editable: true
        },
        {
            title: t('relativeTech'),
            dataIndex: 'relativeTech',
            key: 'relativeTech',
            width: '20%',
            editable: true
        },
        {
            title: t('techIndicators'),
            dataIndex: 'techIndicators',
            key: 'techIndicators',
            editable: true
        },
        {
            title: t('relativeTopic'),
            dataIndex: 'relativeTopic',
            key: 'relativeTopic',
            width: '20%',
            render: (value: string[]) => <Space direction='vertical'>
                {value?.map(ptn => <a key={ptn} onClick={() => gotoDetail(ptn)}>{ptn}</a>)}
            </Space>,
        },
        {
            title: t('Action'),
            dataIndex: 'relativeTopic',
            key: 'relativeTopic',
            width: 150,
            render: renderAction,
        },
    ]
    const columns = defaultColumns.map(col => {
        if (!col.editable) {
            return col
        }
        return {
            ...col,
            onCell: (record: DataType) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave,
            }),
        }
    })

    const onRelativeTopic = async (record: DataType) => {
        const findIndex = dataScourceRef.current.findIndex(item => item.key === record.key)
        if (findIndex !== -1) {
            dataScourceRef.current.splice(findIndex, 1, {
                ...record,
                loading: true
            })
        }
        setDataSource([...dataScourceRef.current])
        const resData = await relativeTopic({
            json_data: {
                [record.key]: {
                    [t('requirementName')]: record.requirementName,
                    [t('functionDesc')]: record.functionDesc,
                    [t('relativeTech')]: record.relativeTech,
                    [t('techIndicators')]: record.techIndicators
                }
            }
        })
        if (findIndex !== -1) {
            dataScourceRef.current.splice(findIndex, 1, {
                ...record,
                loading: false,
                ['relativeTopic']: resData[record.key]
            })
        }
        setDataSource([...dataScourceRef.current])
    }
    const header: (keyof DataType)[] = ['requirementName', 'functionDesc', 'relativeTech', 'techIndicators', 'relativeTopic']
    const onImport = (arr: any[]) => {
        console.log('jsonData: ', arr, arr[1][2])
        const resultArr: DataType[] = []
        const len = arr.length
        if (len > 1) {
            for (let i = 1; i < len; i++) {
                const row = arr[i]
                const rowObj: any = {}
                row.forEach((col: string, index: number) => {
                    rowObj[header[index]] = col ?? ''
                })
                resultArr.push({
                    ...rowObj,
                    key: String(i)
                })
            }
        }
        console.log('resultArr: ', resultArr)
        dataScourceRef.current = [...resultArr]
        setDataSource(resultArr)
    }
    const onExport = () => {
        console.log('onExport: ', dataSource)
        const exportData: any[] = dataSource.map(item => {
            const headerCn: string[] = [t('requirementName'), t('functionDesc'), t('relativeTech'), t('techIndicators'), t('relativeTopic')]
            const result: Record<string, string | string[] | boolean | undefined> = {}
            headerCn.forEach((fieldName: string, index: number) => {
                result[fieldName] = Array.isArray(item[header[index]])
                    ? (item[header[index]] as string[]).join('\n')
                    : item[header[index]]
            })
            return result
        })
        console.log('exportData: ', exportData)
        const worksheet = XLSX.utils.json_to_sheet(exportData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
        XLSX.writeFile(workbook,
            t('process') + (i18n.language !== 'zh' ? ' ' : '') + t('batch')
                + (i18n.language !== 'zh' ? ' ' : '') + t('relative') + '.xlsx'
        )
    }
    const props: UploadProps = {
        name: 'file',
        showUploadList: false,
        beforeUpload: file => {
            console.log('file: ', file)
            if (file) {
                const reader = new FileReader()
                reader.onload = (event: any) => {
                    const data = new Uint8Array(event.target.result)
                    const workbook = XLSX.read(data, { type: 'array' })
                    const firstSheetName = workbook.SheetNames[0]
                    const worksheet = workbook.Sheets[firstSheetName]
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
                    onImport(jsonData)
                }
                reader.readAsArrayBuffer(file)
            }
            return false
        },
    }

    return (<DataSourceContext.Provider value={{
        dataSource,
        setDataSource
    }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: winH - 130, gap: 10 }}>
            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Upload {...props} disabled={btnLoading}>
                        <Button type='primary' icon={<UploadOutlined />} loading={btnLoading}
                            style={{ marginRight: 5 }}
                        >{t('import')}</Button>
                    </Upload>
                    <Popover 
                        placement="topLeft"
                        title={false}
                        content={
                            description$tokens.current.map((token: any, index: number) => {
                                const newDefault = {
                                    ...defaults
                                }
                                const htmlStr = marked.parse(token.raw, {
                                    ...newDefault,
                                    gfm: true,
                                    breaks: true,
                                    renderer
                                })
                                return <div key={index} className='token_box2'
                                    dangerouslySetInnerHTML={{
                                        __html: htmlStr as string
                                    }}
                                >
                                </div>
                            })
                        }
                    >
                        <InfoCircleOutlined style={{ fontSize: 20, color: '#036fc0', verticalAlign: 'middle' }} />
                    </Popover >
                </div>
                <div>
                    <a href="/files/ITMT产品需求描述模板.xlsx" download
                        style={{ marginRight: 5, fontSize: 15, verticalAlign: 'bottom' }}
                    >{t('templateDownload')}</a>
                    <Popover 
                        placement="topRight"
                        title={false}
                        content={<div style={{ width: 250 }}>请在模板文件内填写详细的需求名称、功能描述、相关技术及技术指标。</div>}
                    >
                        <QuestionCircleOutlined style={{ fontSize: 20, color: '#036fc0' }} />
                    </Popover>
                </div>
            </div>
            <div style={{ flex: 1 }}>
                <Table dataSource={dataSource} columns={columns as ColumnTypes}
                    rowClassName={(record: any) => {
                        return `${record.className || ''} editable-row`
                    }}
                    components={components}
                    scroll={{ y: winH - 320 }}
                />
            </div>
            <div style={{ textAlign: 'right' }}>
                <Button type='primary' icon={<DownloadOutlined />} loading={btnLoading}
                    disabled={dataSource.length < 1}
                    onClick={onExport}
                >
                    {t('export')}
                </Button>
            </div>
        </div>
    </DataSourceContext.Provider>)
}

export default BatchRelativeTech
